import { useEffect, useRef, useState, type JSX } from 'react';
import { Clear, Search } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useDebouncedCallback } from 'use-debounce';
import { normalizeScannedSearchInput } from '../../../shared-mui-components/filter/OperatorSearchInputNormalization.ts';

export type SearchText = string | null;

const SCANNED_SEARCH_INPUT_NORMALIZATION_DELAY_MS = 350;

export interface SearchInputFieldProps {
    value: SearchText;
    onChange: (newValue: string | null) => void;
    style?: React.CSSProperties;
    className?: string;
    tooltip: string | JSX.Element;
}

export const SearchInputField = styled(SearchInputFieldComponent, {
    name: 'SearchInputField',
    slot: 'Root',
    overridesResolver: (props, styles) => [
        styles.root,
        props.color === 'primary' && styles.primary,
        props.color === 'secondary' && styles.secondary,
    ],
})(({ theme }) => ({
    margin: theme.spacing(0, 1, 0, 0),
}));

function SearchInputFieldComponent({
    value,
    style,
    onChange,
    className, // passed from styleed
    tooltip,
}: SearchInputFieldProps) {
    const theme = useTheme();
    const searchText = value ?? '';
    const inputRef = useRef<HTMLInputElement>(null);

    const [isFocused, setIsFocused] = useState(false);

    const debouncedSearchChange = useDebouncedCallback(
        (input: HTMLInputElement | HTMLTextAreaElement) => {
            const normalizedValue = normalizeScannedSearchInput(input.value);
            input.value = normalizedValue;
            if (normalizedValue !== searchText) {
                onChange(normalizedValue);
            }
        },
        SCANNED_SEARCH_INPUT_NORMALIZATION_DELAY_MS,
    );

    useEffect(() => {
        const input = inputRef.current;
        if (input && !isFocused) {
            debouncedSearchChange.cancel();
            input.value = searchText;
        }
    }, [debouncedSearchChange, isFocused, searchText]);

    // cancel pending search when the component unmounts
    useEffect(
        () => () => {
            debouncedSearchChange.cancel();
        },
        [debouncedSearchChange],
    );

    return (
        <Tooltip title={tooltip} placement="bottom" arrow disableFocusListener>
            <TextField
                defaultValue={searchText}
                inputRef={inputRef}
                onChange={evt => {
                    debouncedSearchChange(evt.target);
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={evt => {
                    debouncedSearchChange(evt.target);
                    debouncedSearchChange.flush();
                    setIsFocused(false);
                }}
                style={style}
                sx={{
                    color: theme.palette.common?.white,
                }}
                variant="filled"
                hiddenLabel={true}
                className={className}
                color="primary"
                size="small"
                autoFocus
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    sx={{
                                        visibility: value
                                            ? 'visible'
                                            : 'hidden',
                                        color: theme.palette.white.main,
                                        '& :hover': {
                                            color: theme.palette.blue.electric,
                                        },
                                    }}
                                    onClick={() => {
                                        debouncedSearchChange.cancel();
                                        if (inputRef.current) {
                                            inputRef.current.value = '';
                                        }
                                        onChange(null);
                                    }}
                                    edge="end"
                                    size="large"
                                >
                                    <Clear />
                                </IconButton>
                            </InputAdornment>
                        ),
                        style: {
                            backgroundColor: value
                                ? theme.palette.blue.main
                                : undefined,
                            color: 'inherit',
                        },
                        inputProps: {
                            style: {
                                paddingTop: theme.spacing(0.75),
                                paddingBottom: theme.spacing(0.75),
                            },
                        },
                    },
                }}
            />
        </Tooltip>
    );
}
